// The Cost Report's presentation-grade Excel export.
//
// The grid on screen is a working tool — every line, billable and not, three
// levels deep. This is the other thing: the cost report a PM hands over,
// built to be read rather than worked. It comes in two cuts, off the same
// layout and the same math:
//
//   Client — BILLABLE COST ONLY. What goes outside. Non-billable cost is
//            held off every number and stated once, as a footnote.
//   Total  — every cost line, billable and not, with the split carried as
//            its own pair of columns on every row and every line tagged.
//            Internal: it is the cost report plus what the client is not
//            being shown, so the file says so on its face and in its name.
//
// Two bases, one per audience, and never mixed inside a file:
//
//   Client — BILL AMOUNT. The client's cost is what Strike bills, so its cut
//            states every billable line at what it bills for: the amount
//            frozen on its summary if it has been invoiced, the live rate
//            sheet / markup price if it has not. Each equipment line carries
//            its own share of the fuel surcharge, so the surcharge lands in
//            the Equipment column of the row the machine worked under and the
//            total still ties to the invoices.
//   Total  — COST. What the job cost, as JDE posted it. No markup, no rates.
//
// The shape:
//
//   Cost Report   — one row per scope section, the sheet in the screenshot
//   By Cost Code  — every cost code under its section, outlined
//   By Cost Type  — cost code x cost type, the reconciliation grid
//   Cost Detail   — one row per cost line, the backup behind every number
//   Contract      — what the contract held off the invoice, and the clause
//                   (total cut only)
//
// Sections come from the standard WBS discipline lookup (src/db/wbsStandard),
// so "120501 Cable Tray" and "120601 Pwr and Control Conduit" land under
// Electrical without anyone maintaining a second mapping. Cost codes the
// standard doesn't know (legacy jobs, custom phases) collect under
// Unassigned Scope rather than being dropped.
//
// A job with a client WBS (`meta.wbs` — VoltaGrid's Section → Group → Cost
// Code, src/db/wbsVoltaGrid) rolls up on that instead, and its client cut
// stops at the group: the Cost Report sheet and nothing else.

import moment from 'moment';
import { pNum } from '../db/company';
import { standardDisciplineFor } from '../db/wbsStandard';
import { effectLabel, EFFECT_TONE } from './billingContract';
import { clientEquipLabel } from './billingEquipNames';

// Cost classes as columns, in LEMSO order. `key` is the `cls` bucketOf()
// stamps on each transaction; `label` is what the reader reads.
export const CLASS_COLS = [
    { key: 'Labor', label: 'Labor' },
    { key: 'Equipment', label: 'Equipment' },
    { key: 'Materials', label: 'Materials' },
    { key: 'Subcontractors', label: 'Subcontract' },
    { key: 'Other', label: 'Other' },
];

// The line-level backup sheet. Cost-side fields only — no markup, rate or
// bill amount, because a cost report that carried them would invite the
// reader to add them up into an invoice this document does not state.
// `get` pulls the value off a txn as billing.js's txns memo shapes it.
export const DETAIL_COLS = [
    { h: 'Section', w: 24, get: (t) => t.section },
    { h: 'Work Date', w: 12, get: (t) => (t.date ? moment(t.date).format('M/D/YYYY') : ''), fmt: null },
    { h: 'Cost Code', w: 12, get: (t) => t.cc || '' },
    { h: 'Cost Code Description', w: 30, get: (t) => t.ccDesc || '' },
    { h: 'Cost Type', w: 11, get: (t) => t.ct || '' },
    { h: 'Cost Type Description', w: 26, get: (t) => t.ctDesc || '' },
    { h: 'Class', w: 14, get: (t) => t.cls || '' },
    { h: 'Employee / Vendor', w: 26, get: (t) => t.who || '' },
    // Unit and rate sheet item; the JDE tier code stays off the report.
    { h: 'Equipment', w: 28, get: (t) => (t.cls === 'Equipment' || t.unit ? clientEquipLabel({ ...t, who: '' }) : '') },
    { h: 'PO Number', w: 12, get: (t) => t.po || '' },
    { h: 'Vendor Invoice #', w: 16, get: (t) => t.vinvNo || '' },
    { h: 'P-Card', w: 8, get: (t) => (t.pcard ? 'Y' : '') },
    { h: 'Pass-Through', w: 12, get: (t) => (t.pt ? 'Y' : '') },
    { h: 'Description', w: 40, get: (t) => t.ds || '' },
    { h: 'Hours', w: 10, get: (t) => r2(t.hrs), fmt: 'hours' },
    { h: 'Cost', w: 15, get: (t) => r2(t.cost), fmt: 'money' },
    { h: 'Invoice #', w: 13, get: (t) => t.inv || '' },
];

// The total cut's backup carries the one thing the client cut cannot: which
// side of the line each row is on, in words, right next to the section. A
// "Y/N" reads as a flag to sort by; "Billable" / "Not Billable" reads as
// what the row is.
export const BILLABLE_COL = {
    h: 'Billable?', w: 14, get: (t) => (t.nb ? 'Not Billable' : 'Billable'),
};

// A job rolled up on a client WBS carries the group beside the section, so a
// backup row can be traced to the group figure on the Cost Report sheet.
export const GROUP_COL = { h: 'Group', w: 28, get: (t) => t.group || '' };

// On the bill basis the backup's Cost column holds what each line bills for —
// the client's cost. Never both: a file carrying both hands over the margin.
export const BILL_COL = { h: 'Cost', w: 15, get: (t) => r2(t.amt), fmt: 'money' };

export const detailColsFor = (mode, grouped, basis) => [
    DETAIL_COLS[0],
    ...(grouped ? [GROUP_COL] : []),
    ...(mode === 'total' ? [BILLABLE_COL] : []),
    ...DETAIL_COLS.slice(1).map((c) => (basis === 'bill' && c.h === 'Cost' ? BILL_COL : c)),
];

const UNASSIGNED = 'Unassigned Scope';
// Sorts last, behind every real discipline, whatever order they carry.
const UNASSIGNED_ORDER = 99999;

// Palette. One navy for the headers and total band, one wash for the
// highlight rows, one hairline grey for the interior rules — the same three
// tones the bid summary uses, so the two documents read as a set. The two
// status tones are the only colour that carries meaning: green for what
// bills, amber for what does not.
const NAVY = 'FF1F3864';
const NAVY_LIGHT = 'FF2E5496';
const WASH = 'FFD9E2F3';
const WASH_SOFT = 'FFEDF2FA';
const RULE = 'FFB4C6E7';
const HAIR = 'FFDCE6F1';
const INK = 'FF1F2937';
const MUTED = 'FF64748B';
const BILL_INK = 'FF166534';
const NB_INK = 'FF9A3412';
const NB_WASH = 'FFFDF1E7';
const INTERNAL = 'FF7C2D12';

// Accounting formats: the $ hugs the column edge, negatives take parentheses
// and a zero prints as a dash instead of "$0.00" — which is what keeps a
// sparse class grid readable rather than a wall of zeros.
const MONEY = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "-"??_);_(@_)';
// The summary sheet rounds to the dollar: at section scale the cents are
// noise, and the reference bid summary this sheet is cut to match prints
// whole dollars. Every sheet behind it keeps the cents.
const MONEY0 = '_("$"* #,##0_);_("$"* (#,##0);_("$"* "-"_);_(@_)';
const HOURS = '#,##0.0;-#,##0.0;"-"';
const HOURS0 = '#,##0;-#,##0;"-"';
const COUNT = '#,##0;-#,##0;"-"';

const r2 = (n) => Math.round(pNum(n) * 100) / 100;
const r1 = (n) => Math.round(pNum(n) * 10) / 10;

const emptyClassTotals = () => CLASS_COLS.reduce((a, c) => { a[c.key] = 0; return a; }, {});

// A transaction's section: the standard WBS discipline for its cost code.
//
// A job with a client WBS of its own (VoltaGrid: Job → Section → Group → Cost
// Code, src/db/wbsVoltaGrid) passes its lookup as `wbs`, and the report rolls
// up the client's structure instead — section numbers and all, so "Section 4"
// stays Section 4 on a job that has no Piping or Coating cost yet.
const sectionOf = (cc, wbs) => {
    if (wbs) {
        const hit = wbs(cc);
        return hit
            ? { name: hit.name, order: hit.order, no: hit.no, open: !!hit.open, group: hit.group, groupOrder: hit.groupOrder }
            : { name: UNASSIGNED, order: UNASSIGNED_ORDER, no: null, open: false, group: UNASSIGNED, groupOrder: 0 };
    }
    const std = standardDisciplineFor(cc);
    return std
        ? { name: std.discipline, order: std.order, area: std.area }
        : { name: UNASSIGNED, order: UNASSIGNED_ORDER, area: null };
};

// ── the model ────────────────────────────────────────────────────────────
//
// Pure, and exported on its own so the numbers can be asserted without
// going anywhere near ExcelJS.
//
//   mode 'client' (default) — billable only; `nb` lines are summed once into
//                             `heldOut` for the footnote and then left alone.
//   mode 'total'            — every line, with billCost / nbCost carried on
//                             every node so the split reads off any row.
//
// The class columns always hold the row's own total, in both modes, so they
// foot across to Total Cost either way; in total mode the two split columns
// sit beside them rather than replacing them.

export const costReportModel = (txns, opts = {}) => {
    const mode = opts.mode === 'total' ? 'total' : 'client';
    const wbs = typeof opts.wbs === 'function' ? opts.wbs : null;
    const grouped = !!wbs;
    // Which number a line contributes — every `cost` on every node below is
    // on this basis. 'bill' is the client cut's (downloadCostReport picks).
    const basis = opts.basis === 'bill' ? 'bill' : 'cost';
    // `adder` is what bills on top of a line's own amount — the fuel surcharge
    // share (billingFuel's fuelLineAmt), handed in by the caller, who has the
    // fuel index. Bill basis only: it is revenue, and has no cost side.
    const adder = basis === 'bill' && typeof opts.adder === 'function' ? opts.adder : null;
    const amtOf = (t) => (basis === 'bill' ? pNum(t.bill) + (adder ? Number(adder(t)) || 0 : 0) : pNum(t.cost));
    const all = txns || [];
    const rows = mode === 'total' ? all : all.filter((t) => !t.nb);
    const nb = all.filter((t) => t.nb);
    const heldOut = nb.reduce((a, t) => a + pNum(t.cost), 0);
    const heldOutLines = nb.length;

    const bySection = {};
    // The flat line list the Cost Detail sheet prints, each row tagged with
    // the section it rolls into so the backup can be sorted or filtered back
    // to any figure on the summary.
    const lines = [];
    // Section → [group] → cost code nodes for a cost code, made on first touch.
    // Lines make them, and so does a cost code the forecast carries budget or
    // cost-to-go on before any cost has posted to it.
    const nodesFor = (ccRaw, ccDesc) => {
        const cc = ccRaw || '(no cost code)';
        const s = sectionOf(ccRaw, wbs);
        const sec = bySection[s.name] || (bySection[s.name] = {
            name: s.name, order: s.order, lines: 0, hrs: 0, cost: 0, billCost: 0, nbCost: 0,
            cls: emptyClassTotals(), codes: {},
            ...(grouped ? { wbsNo: s.no, open: s.open, groups: {} } : {}),
        });
        // A discipline sorts by the earliest standard order any of its cost
        // codes carries, so a section is placed by its leading phase rather
        // than by whichever line happened to arrive first.
        if (s.order < sec.order) sec.order = s.order;

        const code = sec.codes[cc] || (sec.codes[cc] = {
            cc, desc: ccDesc || '', group: s.group || '', groupOrder: s.groupOrder || 0, lines: 0, hrs: 0, cost: 0, billCost: 0, nbCost: 0,
            cls: emptyClassTotals(), types: {}, planBudget: emptyClassTotals(), planRemaining: emptyClassTotals(),
        });
        // The level between the section and its cost codes, on a job whose
        // WBS has one. It is the deepest level the client cut prints.
        const grp = !grouped ? null : (sec.groups[s.group] || (sec.groups[s.group] = {
            name: s.group, order: s.groupOrder, lines: 0, hrs: 0, cost: 0, billCost: 0, nbCost: 0,
            cls: emptyClassTotals(),
        }));
        if (!code.desc && ccDesc) code.desc = ccDesc;
        return { s, sec, code, grp };
    };
    rows.forEach((t) => {
        const { s, sec, code, grp } = nodesFor(t.cc, t.ccDesc);
        lines.push({ ...t, amt: amtOf(t), section: s.name, group: s.group || '', groupOrder: s.groupOrder || 0 });

        const ct = t.ct || '(no cost type)';
        const type = code.types[ct] || (code.types[ct] = {
            ct, desc: t.ctDesc || '', cls: t.cls || 'Other', lines: 0, hrs: 0, cost: 0, billCost: 0, nbCost: 0,
        });
        if (!type.desc && t.ctDesc) type.desc = t.ctDesc;

        const cost = amtOf(t);
        const hrs = pNum(t.hrs);
        // A class bucketOf() never produces still has to land somewhere —
        // an unrecognised one folds into Other rather than vanishing from
        // the class columns while staying in the row total.
        const k = CLASS_COLS.some((c) => c.key === t.cls) ? t.cls : 'Other';

        [sec, grp, code, type].filter(Boolean).forEach((n) => {
            n.lines += 1; n.hrs += hrs; n.cost += cost;
            if (t.nb) n.nbCost += cost; else n.billCost += cost;
        });
        [sec, grp, code].filter(Boolean).forEach((n) => { n.cls[k] += cost; });
    });

    // ── Budget and EAC ───────────────────────────────────────────────────
    //
    // `plan` is jobPlanByCode's (src/helpers/jobBudgetEac): the forecast's
    // cost budget and cost-to-go (open commitments + ETC) by cost code and
    // class. EAC is what the row states to date plus its cost-to-go, so it
    // foots to the report's own numbers. No plan (forecast data not loaded)
    // leaves both columns blank rather than passing cost to date off as EAC.
    //
    // The total cut states both at cost. The client cut prices them:
    //   Budget — the version's Revenue Budget for the code where it has one
    //            (plan.revenueByCode), with approved CO cost at the bid ratio
    //            on top, so a section reads what the Overview's Bid Budget
    //            reads. Otherwise at the bid's own bill-to-cost ratio (revenue
    //            budget ÷ cost budget), so it totals the revenue budget.
    //            No revenue budget: each class at its ratio so far, below.
    //   EAC    — cost-to-go at each class's bill-to-cost ratio so far
    //            (billable bill ÷ all cost in the class, so what the contract
    //            keeps off the invoice prices out).
    const plan = opts.plan && opts.plan.codes ? opts.plan : null;
    const ratio = emptyClassTotals();
    if (plan) {
        Object.entries(plan.codes).forEach(([cc, p]) => {
            const b = (p && p.budget) || {};
            const rem = (p && p.remaining) || {};
            if (!CLASS_COLS.some((c) => pNum(b[c.key]) || pNum(rem[c.key]))) return;
            const { code } = nodesFor(cc, p.desc);
            CLASS_COLS.forEach((c) => {
                code.planBudget[c.key] += pNum(b[c.key]);
                code.planRemaining[c.key] += pNum(rem[c.key]);
            });
            code.planCo = (code.planCo || 0) + pNum(p.co);
        });
        // A code the revenue budget prices gets its node even before any
        // cost or cost budget lands on it.
        if (basis === 'bill' && plan.revenueByCode) {
            Object.entries(plan.revenueByCode).forEach(([cc, amt]) => {
                if (!pNum(amt)) return;
                const { code } = nodesFor(cc, plan.codes[cc] && plan.codes[cc].desc);
                code.planRevenue = (code.planRevenue || 0) + pNum(amt);
            });
        }
        if (basis === 'bill') {
            const billK = emptyClassTotals();
            const costK = emptyClassTotals();
            let billAll = 0;
            let costAll = 0;
            all.forEach((t) => {
                const k = CLASS_COLS.some((c) => c.key === t.cls) ? t.cls : 'Other';
                const c = pNum(t.cost);
                costK[k] += c; costAll += c;
                if (!t.nb) { const b = amtOf(t); billK[k] += b; billAll += b; }
            });
            const jobRatio = costAll > 0 ? billAll / costAll : 1;
            CLASS_COLS.forEach((c) => { ratio[c.key] = costK[c.key] > 0 ? billK[c.key] / costK[c.key] : jobRatio; });
        } else {
            CLASS_COLS.forEach((c) => { ratio[c.key] = 1; });
        }
    }
    const priced = (byCls) => CLASS_COLS.reduce((a, c) => a + pNum(byCls[c.key]) * ratio[c.key], 0);
    const atCost = (byCls) => CLASS_COLS.reduce((a, c) => a + pNum(byCls[c.key]), 0);
    const allCodes = Object.values(bySection).flatMap((sec) => Object.values(sec.codes));
    const costBudget = allCodes.reduce((a, code) => a + atCost(code.planBudget), 0);
    const revenueBudget = plan ? pNum(plan.revenueBudget) : 0;
    const bidRatio = basis === 'bill' && revenueBudget > 0 && costBudget > 0 ? revenueBudget / costBudget : null;
    const byRevenue = basis === 'bill' && plan && plan.revenueByCode ? plan.revenueByCode : null;
    const budgetOf = (code) => {
        if (byRevenue) return pNum(code.planRevenue) + pNum(code.planCo) * (bidRatio != null ? bidRatio : 1);
        return bidRatio != null ? atCost(code.planBudget) * bidRatio : priced(code.planBudget);
    };
    Object.values(bySection).forEach((sec) => {
        const nodes = [sec, ...(grouped ? Object.values(sec.groups) : [])];
        nodes.forEach((n) => { n.budget = plan ? 0 : null; n.eac = plan ? 0 : null; });
        Object.values(sec.codes).forEach((code) => {
            if (!plan) { code.budget = null; code.eac = null; return; }
            code.budget = budgetOf(code);
            code.eac = code.cost + priced(code.planRemaining);
            const g = grouped ? sec.groups[code.group] : null;
            [sec, g].filter(Boolean).forEach((n) => { n.budget += code.budget; n.eac += code.eac; });
        });
    });
    const planR2 = (n) => (n == null ? null : r2(n));
    // Totals off the unrounded sections, so a client budget scaled to the
    // revenue budget prints that figure to the cent.
    const planTotal = (k) => (plan ? r2(Object.values(bySection).reduce((a, sec) => a + (Number(sec[k]) || 0), 0)) : null);

    const sections = Object.values(bySection)
        .sort((a, b) => (a.order - b.order) || String(a.name).localeCompare(String(b.name)))
        .map((sec, i) => ({
            ...sec,
            // The client's own section number where the WBS carries one;
            // Unassigned Scope has none and prints without.
            no: grouped ? sec.wbsNo : i + 1,
            hrs: r1(sec.hrs),
            cost: r2(sec.cost),
            billCost: r2(sec.billCost),
            nbCost: r2(sec.nbCost),
            budget: planR2(sec.budget),
            eac: planR2(sec.eac),
            ...(grouped ? {
                groups: Object.values(sec.groups)
                    .sort((a, b) => (a.order - b.order) || String(a.name).localeCompare(String(b.name)))
                    .map((g) => ({
                        ...g, hrs: r1(g.hrs), cost: r2(g.cost), billCost: r2(g.billCost), nbCost: r2(g.nbCost),
                        budget: planR2(g.budget), eac: planR2(g.eac),
                    })),
            } : {}),
            codes: Object.values(sec.codes)
                .sort((a, b) => (a.groupOrder - b.groupOrder) || String(a.cc).localeCompare(String(b.cc)))
                .map((c) => ({
                    ...c,
                    hrs: r1(c.hrs),
                    cost: r2(c.cost),
                    billCost: r2(c.billCost),
                    nbCost: r2(c.nbCost),
                    budget: planR2(c.budget),
                    eac: planR2(c.eac),
                    types: Object.values(c.types).sort((a, b) => String(a.ct).localeCompare(String(b.ct))),
                })),
        }));

    const total = sections.reduce((a, s) => {
        CLASS_COLS.forEach((c) => { a.cls[c.key] += s.cls[c.key]; });
        return {
            ...a,
            lines: a.lines + s.lines,
            hrs: a.hrs + s.hrs,
            cost: a.cost + s.cost,
            billCost: a.billCost + s.billCost,
            nbCost: a.nbCost + s.nbCost,
            cls: a.cls,
        };
    }, { lines: 0, hrs: 0, cost: 0, billCost: 0, nbCost: 0, cls: emptyClassTotals() });

    // Section order, then cost code, then date — the order someone reading
    // the summary top to bottom would look for a line in.
    const order = sections.reduce((a, sec, i) => { a[sec.name] = i; return a; }, {});
    lines.sort((a, b) => (order[a.section] - order[b.section])
        || (a.groupOrder - b.groupOrder)
        || String(a.cc).localeCompare(String(b.cc))
        || String(a.date || '').localeCompare(String(b.date || ''))
        || String(a.ct).localeCompare(String(b.ct)));

    // ST/OT labor the rate sheet cannot price bills nothing until it can — on
    // the bill basis that is a line sitting in the report at $0, which whoever
    // sends the file should know about before the client does.
    const noRate = rows.filter((t) => t.noRate);

    return {
        mode,
        basis,
        grouped,
        planned: !!plan,
        noRate: { lines: noRate.length, cost: r2(noRate.reduce((a, t) => a + pNum(t.cost), 0)) },
        sections,
        lines,
        total: {
            ...total,
            hrs: r1(total.hrs),
            cost: r2(total.cost),
            billCost: r2(total.billCost),
            nbCost: r2(total.nbCost),
            budget: planTotal('budget'),
            eac: planTotal('eac'),
        },
        heldOut: r2(heldOut),
        heldOutLines,
    };
};

// ── the workbook ─────────────────────────────────────────────────────────

const setBorder = (cell, edges, color = HAIR) => {
    cell.border = edges.reduce((a, e) => { a[e] = { style: 'thin', color: { argb: color } }; return a; }, {});
};

const fill = (cell, argb) => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } }; };

// The palette, formats and painters, for the Cost & Revenue export
// (billingProfitXlsx), so the three files read as a set.
export const XLSX_STYLE = {
    NAVY, NAVY_LIGHT, WASH, WASH_SOFT, RULE, HAIR, INK, MUTED, BILL_INK, NB_INK, NB_WASH, INTERNAL,
    MONEY, HOURS, COUNT, setBorder, fill,
};

export const buildCostReportWorkbook = async (model, meta = {}) => {
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    wb.creator = 'Fusion Billing';
    wb.created = new Date();

    const isTotal = model.mode === 'total';
    const jobKey = meta.jobKey || '';
    const stamp = moment().format('MMMM D, YYYY');
    const nCls = CLASS_COLS.length;
    // Sec | Description | Hours | <class columns> | [Billable | Not Billable] | Total Cost | Budget | EAC
    const SPLIT_COLS = isTotal ? ['Billable', 'Not Billable'] : [];
    // Budget and EAC sit after Total Cost on every job, on the file's own
    // basis (costReportModel prices them); blank when the forecast wasn't in.
    const PLAN_COLS = ['Budget', 'EAC'];
    const TOTAL_AT = 3 + nCls + SPLIT_COLS.length + 1;
    const LAST = TOTAL_AT + PLAN_COLS.length;
    // Where the two split columns land, so the row painters can tone them.
    const BILL_AT = 3 + nCls + 1;
    const NB_AT = BILL_AT + 1;

    const splitOf = (n) => (isTotal ? [r2(n.billCost), r2(n.nbCost)] : []);
    const planOf = (n) => [n.budget == null ? null : n.budget, n.eac == null ? null : n.eac];

    // Every sheet opens with the same masthead: the job as the title, one
    // line saying what the numbers are, one line saying where they came
    // from. The total cut adds a fourth, banded line — the file is not for
    // the client, and that has to survive being forwarded.
    const masthead = (ws, title, subtitle, note) => {
        const t = ws.addRow([title]);
        t.height = 26;
        t.getCell(1).font = { bold: true, size: 18, color: { argb: isTotal ? INTERNAL : NAVY } };
        ws.mergeCells(t.number, 1, t.number, LAST);

        const s = ws.addRow([subtitle]);
        s.height = 16;
        s.getCell(1).font = { size: 10.5, color: { argb: MUTED } };
        ws.mergeCells(s.number, 1, s.number, LAST);

        const n = ws.addRow([note]);
        n.height = 15;
        n.getCell(1).font = { size: 9.5, italic: true, color: { argb: MUTED } };
        ws.mergeCells(n.number, 1, n.number, LAST);

        if (isTotal) {
            const w = ws.addRow(['INTERNAL — NOT FOR CLIENT DISTRIBUTION. Includes non-billable cost.']);
            w.height = 18;
            for (let i = 1; i <= LAST; i += 1) {
                fill(w.getCell(i), NB_WASH);
                w.getCell(i).font = { bold: true, size: 10, color: { argb: INTERNAL } };
            }
            w.getCell(1).alignment = { vertical: 'middle' };
            ws.mergeCells(w.number, 1, w.number, LAST);
        }
        ws.addRow([]);
        return n.number;
    };

    const provenance = [
        jobKey ? `Job ${jobKey}` : null,
        meta.jobName || null,
        meta.client || null,
        `Prepared ${stamp}`,
        meta.userName ? `by ${meta.userName}` : null,
    ].filter(Boolean).join('   ·   ');

    const title = `${jobKey ? jobKey + ' — ' : ''}${isTotal ? 'TOTAL COST REPORT' : 'COST REPORT'}`;
    // The client cut reads as the client's cost report: what Strike bills IS
    // their cost, so its labels say "cost" and nothing about billable, markup
    // or rates — that vocabulary is Strike's working, and it stays in the
    // total cut.
    const TOTAL_BAND = isTotal ? 'TOTAL COST (ALL LINES)' : 'TOTAL COST';
    const subtitle = `${meta.jobName ? meta.jobName + '. ' : ''}${isTotal
        ? 'All cost by scope section, billable and non-billable side by side. Cost as posted — no markup, rates or fee.'
        : 'Cost by scope section.'}`;

    // ── Sheet 1: the cost report itself ──────────────────────────────────
    const ws = wb.addWorksheet('Cost Report', {
        views: [{ showGridLines: false }],
        pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0, margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 } },
    });
    ws.columns = [
        { width: 6 }, { width: 46 }, { width: 11 },
        ...CLASS_COLS.map(() => ({ width: 16 })),
        ...SPLIT_COLS.map(() => ({ width: 17 })),
        { width: 18 },
        ...PLAN_COLS.map(() => ({ width: 18 })),
    ];

    masthead(ws, title, subtitle, provenance);

    const headRow = ws.addRow([
        'Sec', 'Description', 'Hours',
        ...CLASS_COLS.map((c) => c.label),
        ...SPLIT_COLS,
        'Total Cost',
        ...PLAN_COLS,
    ]);
    headRow.height = 30;
    for (let i = 1; i <= LAST; i += 1) {
        const c = headRow.getCell(i);
        fill(c, NAVY);
        c.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
        c.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        setBorder(c, ['top', 'left', 'bottom', 'right'], NAVY_LIGHT);
    }

    // On a job with a client WBS the groups hang under their section as an
    // outline, +/- at the section row. They are the last level this sheet
    // goes to, and a section opens to them only where the client's own WBS
    // workbook was saved open — the rest print as the one section row.
    if (model.grouped) {
        ws.properties.outlineProperties = { summaryBelow: false, summaryRight: false };
        ws.properties.outlineLevelRow = 1;
    }

    model.sections.forEach((s, i) => {
        const row = ws.addRow([
            s.no || '',
            s.name,
            s.hrs,
            ...CLASS_COLS.map((c) => r2(s.cls[c.key])),
            ...splitOf(s),
            s.cost,
            ...planOf(s),
        ]);
        row.height = 19;
        for (let cIdx = 1; cIdx <= LAST; cIdx += 1) {
            const c = row.getCell(cIdx);
            c.font = { size: 11, color: { argb: INK } };
            // The hairline vanishes against the wash, and two collapsed
            // sections in a row would read as one block — so a washed row
            // takes the stronger rule.
            setBorder(c, ['left', 'right', 'bottom'], model.grouped ? RULE : HAIR);
            // With groups under them, every section row carries the same
            // wash and the groups stay white — the tint says "this is a
            // section", so it cannot alternate. A flat list of sections has
            // no such level to mark, and keeps the faint band every other row
            // that lets the eye track across eight columns.
            if (model.grouped) fill(c, WASH);
            else if (i % 2 === 1) fill(c, WASH_SOFT);
        }
        row.getCell(1).alignment = { horizontal: 'center' };
        row.getCell(3).numFmt = HOURS0;
        for (let cIdx = 4; cIdx <= LAST; cIdx += 1) row.getCell(cIdx).numFmt = MONEY0;
        if (isTotal) {
            row.getCell(BILL_AT).font = { size: 11, bold: true, color: { argb: BILL_INK } };
            row.getCell(NB_AT).font = { size: 11, color: { argb: NB_INK } };
            if (s.nbCost) fill(row.getCell(NB_AT), NB_WASH);
        }
        row.getCell(TOTAL_AT).font = { size: 11, bold: true, color: { argb: INK } };

        (s.groups || []).forEach((g) => {
            const gr = ws.addRow([
                '', g.name, g.hrs,
                ...CLASS_COLS.map((c) => r2(g.cls[c.key])),
                ...splitOf(g),
                g.cost,
                ...planOf(g),
            ]);
            gr.height = 17;
            gr.outlineLevel = 1;
            gr.hidden = !s.open;
            for (let cIdx = 1; cIdx <= LAST; cIdx += 1) {
                const c = gr.getCell(cIdx);
                c.font = { size: 10, color: { argb: MUTED } };
                setBorder(c, ['left', 'right', 'bottom']);
            }
            gr.getCell(2).alignment = { indent: 2 };
            gr.getCell(2).font = { size: 10, color: { argb: INK } };
            gr.getCell(3).numFmt = HOURS0;
            for (let cIdx = 4; cIdx <= LAST; cIdx += 1) gr.getCell(cIdx).numFmt = MONEY0;
            if (isTotal) {
                gr.getCell(BILL_AT).font = { size: 10, color: { argb: BILL_INK } };
                gr.getCell(NB_AT).font = { size: 10, color: { argb: NB_INK } };
            }
        });
    });

    // The total band — the one row that reverses out, as on the bid summary.
    const totalRow = ws.addRow([
        '', `${jobKey || 'JOB'} — ${TOTAL_BAND}`, model.total.hrs,
        ...CLASS_COLS.map((c) => r2(model.total.cls[c.key])),
        ...splitOf(model.total),
        model.total.cost,
        ...planOf(model.total),
    ]);
    totalRow.height = 24;
    for (let i = 1; i <= LAST; i += 1) {
        const c = totalRow.getCell(i);
        fill(c, NAVY);
        c.font = { bold: true, size: 11.5, color: { argb: 'FFFFFFFF' } };
        c.alignment = { vertical: 'middle' };
        setBorder(c, ['top', 'left', 'bottom', 'right'], NAVY_LIGHT);
    }
    totalRow.getCell(3).numFmt = HOURS0;
    for (let i = 4; i <= LAST; i += 1) totalRow.getCell(i).numFmt = MONEY0;

    // Footer band and scope note — total cut only. They explain where the
    // billable line falls, which is Strike's working; the client cut ends at
    // its total band.
    if (isTotal) {
        const footer = (label, value, fmt, tone) => {
            // The value sits under Total Cost; the Budget and EAC columns
            // stay empty on these rows.
            const row = ws.addRow(['', label, ...new Array(TOTAL_AT - 3).fill(''), value]);
            row.height = 19;
            for (let i = 1; i <= LAST; i += 1) {
                const c = row.getCell(i);
                fill(c, WASH);
                c.font = { bold: i === TOTAL_AT, size: 10.5, color: { argb: tone || NAVY } };
                setBorder(c, ['left', 'right', 'bottom'], RULE);
            }
            row.getCell(TOTAL_AT).numFmt = fmt;
            return row;
        };
        footer('Cost lines included (billable and non-billable)', model.total.lines, COUNT);
        footer('Billable cost — the cost of the lines the client cost report prices', model.total.billCost, MONEY0, BILL_INK);
        footer('Non-billable cost — held off the client cost report', model.total.nbCost, MONEY0, NB_INK);

        ws.addRow([]);
        const note = ws.addRow(['This report states cost only, for every line on the job. Billable status follows the contract rules and any per-line overrides recorded in Fusion; the client cost report carries the billable lines alone, priced at the contract rates. Invoice value, markup and rate-sheet pricing are shown on the billing summaries.']);
        note.getCell(1).font = { size: 9, italic: true, color: { argb: MUTED } };
        note.getCell(1).alignment = { wrapText: true, vertical: 'top' };
        note.height = 26;
        ws.mergeCells(note.number, 1, note.number, LAST);
    }

    // The three sheets below go past the group level — cost codes, cost types,
    // the lines themselves. On a job with a client WBS the client cut stops at
    // the group, so they are left out of that file altogether rather than
    // tucked behind the summary; the total cut, which stays inside, keeps them.
    const backup = isTotal || !model.grouped;
    if (backup) {
        // ── Sheet 2: every cost code, under its section ───────────────────────
        const wsC = wb.addWorksheet('By Cost Code', {
            views: [{ showGridLines: false }],
            pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
        });
        wsC.columns = ws.columns.map((c) => ({ ...c }));
        masthead(
            wsC,
            `${jobKey ? jobKey + ' — ' : ''}${isTotal ? 'TOTAL COST REPORT DETAIL' : 'COST REPORT DETAIL'}`,
            `${isTotal ? 'All cost' : 'Cost'} by cost code, grouped under the scope section it rolls into.`,
            provenance,
        );
        const headC = wsC.addRow([
            'Sec', 'Cost Code', 'Hours',
            ...CLASS_COLS.map((c) => c.label),
            ...SPLIT_COLS,
            'Total Cost',
            ...PLAN_COLS,
        ]);
        headC.height = 30;
        for (let i = 1; i <= LAST; i += 1) {
            const c = headC.getCell(i);
            fill(c, NAVY);
            c.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
            c.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            setBorder(c, ['top', 'left', 'bottom', 'right'], NAVY_LIGHT);
        }
        wsC.views = [{ state: 'frozen', ySplit: headC.number, showGridLines: false }];
        if (model.grouped) wsC.properties.outlineLevelRow = 2;

        model.sections.forEach((s) => {
            const sr = wsC.addRow([
                s.no, s.name, s.hrs,
                ...CLASS_COLS.map((c) => r2(s.cls[c.key])),
                ...splitOf(s),
                s.cost,
                ...planOf(s),
            ]);
            sr.height = 20;
            for (let i = 1; i <= LAST; i += 1) {
                const c = sr.getCell(i);
                fill(c, WASH);
                c.font = { bold: true, size: 11, color: { argb: NAVY } };
                setBorder(c, ['top', 'left', 'bottom', 'right'], RULE);
            }
            sr.getCell(1).alignment = { horizontal: 'center' };
            sr.getCell(3).numFmt = HOURS;
            for (let i = 4; i <= LAST; i += 1) sr.getCell(i).numFmt = MONEY;
            if (isTotal) {
                sr.getCell(BILL_AT).font = { bold: true, size: 11, color: { argb: BILL_INK } };
                sr.getCell(NB_AT).font = { bold: true, size: 11, color: { argb: NB_INK } };
            }

            const codeRows = (codes, level) => codes.forEach((code) => {
                const row = wsC.addRow([
                    '', `${code.cc}${code.desc ? ' — ' + code.desc : ''}`, code.hrs,
                    ...CLASS_COLS.map((c) => r2(code.cls[c.key])),
                    ...splitOf(code),
                    code.cost,
                    ...planOf(code),
                ]);
                // Collapsible under the section band, so the sheet can be read at
                // section level and opened where a number is questioned.
                row.outlineLevel = level;
                for (let i = 1; i <= LAST; i += 1) {
                    const c = row.getCell(i);
                    c.font = { size: 10.5, color: { argb: INK } };
                    setBorder(c, ['left', 'right', 'bottom']);
                }
                row.getCell(2).alignment = { indent: level * 2 };
                row.getCell(3).numFmt = HOURS;
                for (let i = 4; i <= LAST; i += 1) row.getCell(i).numFmt = MONEY;
                if (isTotal) {
                    row.getCell(BILL_AT).font = { size: 10.5, color: { argb: BILL_INK } };
                    row.getCell(NB_AT).font = { size: 10.5, color: { argb: NB_INK } };
                    // A cost code carrying non-billable cost is what someone opens
                    // this sheet to find, so it is tinted; one that is wholly
                    // non-billable is tinted the whole way across.
                    if (code.nbCost) fill(row.getCell(NB_AT), NB_WASH);
                    if (code.nbCost && !code.billCost) {
                        for (let i = 1; i <= LAST; i += 1) fill(row.getCell(i), NB_WASH);
                    }
                }
            });

            if (!model.grouped) { codeRows(s.codes, 1); return; }
            // Section → group → cost code: the group row is the figure the Cost
            // Report sheet prints, the codes under it are what it is made of.
            s.groups.forEach((g) => {
                const gr = wsC.addRow([
                    '', g.name, g.hrs,
                    ...CLASS_COLS.map((c) => r2(g.cls[c.key])),
                    ...splitOf(g),
                    g.cost,
                    ...planOf(g),
                ]);
                gr.outlineLevel = 1;
                for (let i = 1; i <= LAST; i += 1) {
                    const c = gr.getCell(i);
                    fill(c, WASH_SOFT);
                    c.font = { bold: true, size: 10.5, color: { argb: INK } };
                    setBorder(c, ['left', 'right', 'bottom']);
                }
                gr.getCell(2).alignment = { indent: 2 };
                gr.getCell(3).numFmt = HOURS;
                for (let i = 4; i <= LAST; i += 1) gr.getCell(i).numFmt = MONEY;
                if (isTotal) {
                    gr.getCell(BILL_AT).font = { bold: true, size: 10.5, color: { argb: BILL_INK } };
                    gr.getCell(NB_AT).font = { bold: true, size: 10.5, color: { argb: NB_INK } };
                }
                codeRows(s.codes.filter((c) => c.group === g.name), 2);
            });
        });

        const totC = wsC.addRow([
            '', TOTAL_BAND, model.total.hrs,
            ...CLASS_COLS.map((c) => r2(model.total.cls[c.key])),
            ...splitOf(model.total),
            model.total.cost,
            ...planOf(model.total),
        ]);
        totC.height = 22;
        for (let i = 1; i <= LAST; i += 1) {
            const c = totC.getCell(i);
            fill(c, NAVY);
            c.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
            setBorder(c, ['top', 'left', 'bottom', 'right'], NAVY_LIGHT);
        }
        totC.getCell(3).numFmt = HOURS;
        for (let i = 4; i <= LAST; i += 1) totC.getCell(i).numFmt = MONEY;

        // ── Sheet 3: cost code x cost type, the reconciliation grid ───────────
        const wsT = wb.addWorksheet('By Cost Type', { views: [{ state: 'frozen', ySplit: 1 }] });
        const tHead = [
            'Section', ...(model.grouped ? ['Group'] : []), 'Cost Code', 'Cost Code Description', 'Cost Type', 'Cost Type Description', 'Class',
            ...(isTotal ? ['Billable?'] : []),
            'Lines', 'Hours', 'Cost',
            ...(isTotal ? ['Billable', 'Not Billable'] : []),
        ];
        wsT.columns = [
            { width: 26 }, ...(model.grouped ? [{ width: 28 }] : []), { width: 12 }, { width: 34 }, { width: 11 }, { width: 30 }, { width: 15 },
            ...(isTotal ? [{ width: 14 }] : []),
            { width: 8 }, { width: 10 }, { width: 16 },
            ...(isTotal ? [{ width: 16 }, { width: 16 }] : []),
        ];
        const th = wsT.addRow(tHead);
        th.height = 20;
        th.eachCell((c) => {
            fill(c, NAVY);
            c.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
            c.alignment = { vertical: 'middle' };
        });
        const tLines = tHead.indexOf('Lines') + 1;
        const tHours = tHead.indexOf('Hours') + 1;
        const tCost = tHead.indexOf('Cost') + 1;
        const tFlag = tHead.indexOf('Billable?') + 1;
        // A bucket is mixed when both sides carry cost — worth saying, because
        // "Mixed" is the row someone has to open in the backup to settle.
        const flagOf = (n) => (n.nbCost && n.billCost ? 'Mixed' : n.nbCost ? 'Not Billable' : 'Billable');
        model.sections.forEach((s) => s.codes.forEach((code) => code.types.forEach((ty) => {
            const row = wsT.addRow([
                s.name, ...(model.grouped ? [code.group] : []), code.cc, code.desc, ty.ct, ty.desc, ty.cls,
                ...(isTotal ? [flagOf(ty)] : []),
                ty.lines, r1(ty.hrs), r2(ty.cost),
                ...(isTotal ? [r2(ty.billCost), r2(ty.nbCost)] : []),
            ]);
            row.getCell(tLines).numFmt = COUNT;
            row.getCell(tHours).numFmt = HOURS;
            row.getCell(tCost).numFmt = MONEY;
            if (isTotal) {
                row.getCell(tCost + 1).numFmt = MONEY;
                row.getCell(tCost + 2).numFmt = MONEY;
                row.getCell(tCost + 1).font = { size: 11, color: { argb: BILL_INK } };
                row.getCell(tCost + 2).font = { size: 11, color: { argb: NB_INK } };
                row.getCell(tFlag).font = { bold: true, size: 10, color: { argb: ty.billCost ? BILL_INK : NB_INK } };
                if (!ty.billCost) for (let i = 1; i <= tHead.length; i += 1) fill(row.getCell(i), NB_WASH);
            }
            row.eachCell((c) => setBorder(c, ['bottom']));
        })));
        const totT = wsT.addRow([
            'TOTAL', ...(model.grouped ? [''] : []), '', '', '', '', '',
            ...(isTotal ? [''] : []),
            model.total.lines, model.total.hrs, model.total.cost,
            ...(isTotal ? [model.total.billCost, model.total.nbCost] : []),
        ]);
        totT.font = { bold: true };
        totT.getCell(tLines).numFmt = COUNT;
        totT.getCell(tHours).numFmt = HOURS;
        totT.getCell(tCost).numFmt = MONEY;
        if (isTotal) {
            totT.getCell(tCost + 1).numFmt = MONEY;
            totT.getCell(tCost + 2).numFmt = MONEY;
        }
        totT.eachCell((c) => setBorder(c, ['top'], NAVY));
        if (wsT.rowCount > 1) wsT.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: tHead.length } };

        // ── Sheet 4: one row per cost line — the backup ───────────────────────
        //
        // Every transaction behind the three sheets above, so a number that gets
        // questioned can be opened down to the timecard, the PO line or the
        // voucher without leaving the workbook.
        const cols = detailColsFor(model.mode, model.grouped, model.basis);
        const wsD = wb.addWorksheet('Cost Detail', { views: [{ state: 'frozen', ySplit: 1 }] });
        wsD.columns = cols.map((c) => ({ width: c.w }));
        const dHead = wsD.addRow(cols.map((c) => c.h));
        dHead.height = 22;
        dHead.eachCell((c) => {
            fill(c, NAVY);
            c.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
            c.alignment = { vertical: 'middle', wrapText: true };
        });
        const dFlag = cols.findIndex((c) => c.h === BILLABLE_COL.h) + 1;
        model.lines.forEach((t, i) => {
            const row = wsD.addRow(cols.map((c) => c.get(t)));
            cols.forEach((c, idx) => {
                const cell = row.getCell(idx + 1);
                if (c.fmt === 'money') cell.numFmt = MONEY;
                if (c.fmt === 'hours') cell.numFmt = HOURS;
                cell.font = { size: 10, color: { argb: INK } };
                setBorder(cell, ['bottom']);
                // Non-billable lines carry the amber wash the whole way across —
                // on a few thousand rows that is what makes the split readable
                // without sorting. Everything else keeps the faint banding.
                if (isTotal && t.nb) fill(cell, NB_WASH);
                else if (i % 2 === 1) fill(cell, WASH_SOFT);
            });
            if (dFlag) {
                row.getCell(dFlag).font = { size: 10, bold: true, color: { argb: t.nb ? NB_INK : BILL_INK } };
            }
        });
        const totD = wsD.addRow(cols.map((c) => (
            c.h === 'Section' ? 'TOTAL' : c.h === 'Hours' ? model.total.hrs : c.h === 'Cost' ? model.total.cost : ''
        )));
        totD.font = { bold: true };
        cols.forEach((c, idx) => {
            const cell = totD.getCell(idx + 1);
            if (c.fmt === 'money') cell.numFmt = MONEY;
            if (c.fmt === 'hours') cell.numFmt = HOURS;
            setBorder(cell, ['top'], NAVY);
        });
        if (model.lines.length) {
            wsD.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: cols.length } };
        }
    }

    // ── Sheet 5: the contract, when the rules are on ──────────────────────
    // Internal only: it lists what was held off the invoice and at what cost,
    // which is Strike's working, not something the client file carries.
    const groups = (meta.contract && meta.contract.groups) || [];
    if (groups.length && isTotal) {
        const wsK = wb.addWorksheet('Contract', { views: [{ state: 'frozen', ySplit: 1 }] });
        const kHead = ['On the invoice?', 'Category', 'Cost Types', 'Clause', 'Note', 'Lines', 'Cost', 'Already Invoiced'];
        wsK.columns = [{ width: 24 }, { width: 40 }, { width: 30 }, { width: 34 }, { width: 44 }, { width: 8 }, { width: 16 }, { width: 18 }];
        const kh = wsK.addRow(kHead);
        kh.height = 20;
        kh.eachCell((c) => {
            fill(c, NAVY);
            c.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
            c.alignment = { vertical: 'middle' };
        });
        groups.forEach((g) => {
            const row = wsK.addRow([
                effectLabel(g.effect),
                g.label,
                (g.cts || []).map((c) => (c.desc ? `${c.ct} ${c.desc}` : c.ct)).join('\n'),
                g.clause, g.note || '', g.n, r2(g.cost), r2(g.billedCost),
            ]);
            const tone = (EFFECT_TONE[g.effect] || EFFECT_TONE.warn)[0];
            row.getCell(1).font = { bold: true, color: { argb: `FF${tone.slice(1).toUpperCase()}` } };
            row.getCell(3).alignment = { wrapText: true, vertical: 'top' };
            row.getCell(5).alignment = { wrapText: true, vertical: 'top' };
            row.getCell(6).numFmt = COUNT;
            row.getCell(7).numFmt = MONEY;
            row.getCell(8).numFmt = MONEY;
            row.eachCell((c) => setBorder(c, ['bottom']));
        });
    }

    return wb;
};

// ExcelJS 4.4 writes <sheetPr> with pageSetUpPr ahead of outlinePr; the schema
// wants them the other way round, and Excel calls a sheet that has both
// (fit-to-page plus the summary-row-above outline the WBS cut uses) corrupt.
// So the buffer is written, then the two elements are put in order.
export const costReportBuffer = async (wb) => {
    const raw = await wb.xlsx.writeBuffer();
    const JSZip = (await import('jszip')).default;
    const zip = await JSZip.loadAsync(raw);
    const sheets = Object.keys(zip.files).filter((n) => /^xl\/worksheets\/[^/]+\.xml$/.test(n));
    let touched = false;
    await Promise.all(sheets.map(async (name) => {
        const xml = await zip.file(name).async('string');
        const fixed = xml.replace(/(<pageSetUpPr[^>]*\/>)(<outlinePr[^>]*\/>)/, '$2$1');
        if (fixed !== xml) { zip.file(name, fixed); touched = true; }
    }));
    return touched ? zip.generateAsync({ type: 'uint8array', compression: 'DEFLATE' }) : raw;
};

// Build and hand the file to the browser. Returns the filename so a caller
// can report what it produced. The total cut says INTERNAL in its name: the
// two files share a layout, and the filename is the only thing telling them
// apart in a downloads folder.
export const downloadCostReport = async (txns, meta = {}) => {
    const mode = meta.mode === 'total' ? 'total' : 'client';
    // The client cut states bill amounts, the internal cut states cost.
    const model = costReportModel(txns, {
        mode, wbs: meta.wbs, basis: mode === 'client' ? 'bill' : 'cost', adder: meta.adder, plan: meta.plan,
    });
    const wb = await buildCostReportWorkbook(model, meta);
    const buffer = await costReportBuffer(wb);
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const name = `${mode === 'total' ? 'Cost_Report_INTERNAL_Total' : 'Cost_Report_Client'}_${meta.jobKey || 'job'}_${moment().format('YYYY-MM-DD')}.xlsx`;
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
    return name;
};
